/**
 * Job Queue Types
 * Defines all types related to the job queue management system
 */

// Job status enum
export enum JobStatus {
  READY = 'ready',           // Job created, waiting for API to process
  PROCESSING = 'processing', // API is currently processing the job
  PROCESSED = 'processed',   // Job processing complete, output uploaded
  EXTRACTED = 'extracted',   // Output zip extracted to folder
  SUCCESS = 'success',       // Job completed successfully
  ERROR = 'error',           // Job encountered an error
}

// Job condition/schedule enum
export enum JobCondition {
  ONCE = 'once',             // Run once immediately
  DAILY = 'daily',           // Run daily
  WEEKLY = 'weekly',         // Run weekly
  MONTHLY = 'monthly',       // Run monthly
  CUSTOM = 'custom',         // Custom cron expression
}

// Job category enum
export enum JobCategory {
  VIDEO_CONVERSION = 'video_conversion',
  IMAGE_UPSCALE = 'image_upscale',
  IMAGE_RESIZE = 'image_resize',
  CUSTOM = 'custom',
}

// Job template type
export interface JobTemplate {
  id: string;
  name: string;
  category: JobCategory;
  description: string;
  inputFileTypes: string[];        // e.g., ['.mp4', '.avi']
  outputFormat?: string;            // e.g., 'hls', 'webp'
  parameters: Record<string, any>;  // Template-specific params
  icon?: string;
}

// File object
export interface JobFile {
  id: string;
  name: string;
  size: number;
  type: string;
  path?: string;
  uploadedAt: string;
}

// Job object
export interface Job {
  id: string;
  name: string;
  templateId: string;
  category: JobCategory;
  description?: string;
  status: JobStatus;
  condition: JobCondition;
  
  // File and processing info
  inputFile?: JobFile;
  inputFileId?: string;
  outputFile?: JobFile;
  parameters: Record<string, any>;
  
  // Parent-child relationships
  parentJobId?: string;  // If this is a child job
  childJobIds?: string[]; // If this is a parent job with multiple files
  
  // Scheduling
  scheduledFor?: string;  // ISO date string
  customCron?: string;    // For CUSTOM condition
  
  // Status tracking
  createdAt: string;
  updatedAt: string;
  processedAt?: string;
  completedAt?: string;
  
  // Error handling
  errorMessage?: string;
  errorStack?: string;
  
  // Metadata
  userId?: string;
  createdBy?: string;
  tags?: string[];
  
  // Security
  isPublic?: boolean;      // Is file public or private
  allowedUserIds?: string[]; // Specific users who can access (if private)
}

// Job creation payload
export interface CreateJobPayload {
  name: string;
  templateId: string;
  category: JobCategory;
  description?: string;
  condition: JobCondition;
  inputFileId?: string;
  parameters: Record<string, any>;
  scheduledFor?: string;
  customCron?: string;
  tags?: string[];
  isParentJob?: boolean;
  isChildJob?: boolean;
  parentJobName?: string;
}

// Job update payload
export interface UpdateJobPayload {
  name?: string;
  description?: string;
  condition?: JobCondition;
  inputFileId?: string;
  parameters?: Record<string, any>;
  scheduledFor?: string;
  customCron?: string;
  tags?: string[];
}

// Job filter criteria
export interface JobFilter {
  status?: JobStatus[];
  category?: JobCategory[];
  templateId?: string;
  searchText?: string;
  dateRange?: {
    from: string;
    to: string;
  };
  tags?: string[];
}

// Job list response
export interface JobListResponse {
  jobs: Job[];
  total: number;
  page: number;
  pageSize: number;
}

// Job detail response (with more metadata)
export interface JobDetailResponse extends Job {
  processLog?: string;
  processingProgress?: number;  // 0-100
}

// API response types
export interface JobApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}
